/**
 * Frases do domínio que aparecem na interface (na língua de quem a usa, não na do guia):
 * papéis, planos e limites, marcas da execução, datas relativas, poupança estimada e
 * mensagens de erro da API por código.
 */
import { formatNumber, type Locale } from '@nolens/i18n';
import type { ErrorCode, Role } from './api';
import { AI_MONTHLY_LIMIT, TEAM_MAX_EDITORS, type PlanId, type PlanLimitReason } from './plans';
import type { DateFormat, DatePreset, RunMark } from './run';

type Table<K extends string> = Record<K, string>;
interface Texts {
  roles: Table<Role>;
  plans: Table<PlanId>;
  planLimits: Table<PlanLimitReason>;
  runMarks: Table<RunMark>;
  datePresets: Table<DatePreset>;
  dateFormats: Table<DateFormat>;
  errors: Table<ErrorCode>;
  savingsClaim: string;
  stepN: (n: number) => string;
}

const EN: Texts = {
  roles: { owner: 'Owner', editor: 'Editor', viewer: 'Viewer' },
  plans: { free: 'Free', pro: 'Pro', team: 'Pro Team', expired: 'No plan' },
  planLimits: {
    guides: 'You have reached the 10 guides of the Free plan. With Pro, guides are unlimited.',
    storage: 'You have reached the screenshot storage of your plan.',
    publicLinks: 'Public links are part of the Pro and Pro Team plans.',
    brand: 'Company branding is part of the Pro and Pro Team plans.',
    ai: 'AI suggestions are part of the Pro and Pro Team plans.',
    aiQuota: `You have used this month’s ${AI_MONTHLY_LIMIT} AI suggestions for this space. They are available again on the 1st.`,
    readers: 'The Pro plan includes up to 3 viewers. For more, choose Pro Team.',
    editors: 'To have other editors, choose Pro Team.',
    editorsMax: `Pro Team goes up to ${TEAM_MAX_EDITORS} editors. For more, contact us about a company plan.`,
    expired:
      'This team has no active plan: its guides can be viewed, but not created or changed. Choose a plan to activate it.',
  },
  runMarks: {
    auto: 'The extension does it',
    confirm: 'Asks for confirmation',
    user: 'The person does it',
  },
  datePresets: {
    today: 'Today',
    yesterday: 'Yesterday',
    days_ago_7: '7 days ago',
    days_ago_30: '30 days ago',
    week_start: 'Start of this week (Monday)',
    prev_week_start: 'Start of last week',
    prev_week_end: 'End of last week (Sunday)',
    month_start: 'First day of this month',
    prev_month_start: 'First day of last month',
    prev_month_end: 'Last day of last month',
    year_start: 'First day of this year',
  },
  dateFormats: {
    'dd/mm/aaaa': 'dd/mm/yyyy',
    'aaaa-mm-dd': 'yyyy-mm-dd',
    'mm/dd/aaaa': 'mm/dd/yyyy',
    'dd-mm-aaaa': 'dd-mm-yyyy',
    'dd.mm.aaaa': 'dd.mm.yyyy',
  },
  errors: {
    UNAUTHENTICATED: 'Your session has ended. Please sign in again.',
    FORBIDDEN: "You don't have permission to do this.",
    NOT_FOUND: 'Not found.',
    VALIDATION_FAILED: 'Some fields need fixing.',
    CONFLICT: 'This conflicts with existing data.',
    VERSION_CONFLICT: 'The guide was changed in the meantime.',
    PAYLOAD_TOO_LARGE: 'The file is too large.',
    RATE_LIMITED: 'Too many requests. Try again in a moment.',
    UPSTREAM_UNAVAILABLE: 'An external service is unavailable. Try again later.',
    NOT_CONFIGURED: 'This feature is not active yet.',
    PLAN_LIMIT: 'This is not part of your plan.',
    EXTENSION_OUTDATED:
      'This version of the extension is no longer supported. Update the extension.',
    APP_OUTDATED: 'This version of the Mac app is no longer supported. Update the app.',
    INTERNAL: 'Something went wrong. Try again.',
  },
  savingsClaim:
    'Estimate: 2 min saved per documented step (reference: 120 s per step done by hand).',
  stepN: (n) => `Step ${n}`,
};

const PT: Texts = {
  roles: { owner: 'Dono', editor: 'Editor', viewer: 'Leitor' },
  plans: { free: 'Grátis', pro: 'Pro', team: 'Pro Equipa', expired: 'Sem plano' },
  planLimits: {
    guides: 'Atingiu os 10 guias do plano Grátis. Com o Pro, os guias são ilimitados.',
    storage: 'Atingiu o espaço para capturas do seu plano.',
    publicLinks: 'As ligações públicas fazem parte dos planos Pro e Pro Equipa.',
    brand: 'A marca da empresa faz parte dos planos Pro e Pro Equipa.',
    ai: 'As sugestões por IA fazem parte dos planos Pro e Pro Equipa.',
    aiQuota: `Já usou as ${AI_MONTHLY_LIMIT} sugestões por IA deste mês neste espaço. Voltam a estar disponíveis no dia 1.`,
    readers: 'O plano Pro inclui até 3 leitores. Para mais, escolha o Pro Equipa.',
    editors: 'Para ter outros editores, escolha o Pro Equipa.',
    editorsMax: `O Pro Equipa vai até ${TEAM_MAX_EDITORS} editores. Para mais, fale connosco sobre um plano para empresas.`,
    expired:
      'Esta equipa não tem um plano ativo: os guias podem ser vistos, mas não criados nem alterados. Escolha um plano para a ativar.',
  },
  runMarks: { auto: 'A extensão faz', confirm: 'Pede confirmação', user: 'A pessoa faz' },
  datePresets: {
    today: 'Hoje',
    yesterday: 'Ontem',
    days_ago_7: 'Há 7 dias',
    days_ago_30: 'Há 30 dias',
    week_start: 'Início desta semana (segunda)',
    prev_week_start: 'Início da semana passada',
    prev_week_end: 'Fim da semana passada (domingo)',
    month_start: '1.º dia deste mês',
    prev_month_start: '1.º dia do mês passado',
    prev_month_end: 'Último dia do mês passado',
    year_start: '1.º dia deste ano',
  },
  dateFormats: {
    'dd/mm/aaaa': 'dd/mm/aaaa',
    'aaaa-mm-dd': 'aaaa-mm-dd',
    'mm/dd/aaaa': 'mm/dd/aaaa',
    'dd-mm-aaaa': 'dd-mm-aaaa',
    'dd.mm.aaaa': 'dd.mm.aaaa',
  },
  errors: {
    UNAUTHENTICATED: 'A sessão terminou. Entre outra vez.',
    FORBIDDEN: 'Não tem permissão para fazer isto.',
    NOT_FOUND: 'Não encontrado.',
    VALIDATION_FAILED: 'Há campos por corrigir.',
    CONFLICT: 'Isto entra em conflito com dados que já existem.',
    VERSION_CONFLICT: 'O guia foi alterado entretanto.',
    PAYLOAD_TOO_LARGE: 'O ficheiro é demasiado grande.',
    RATE_LIMITED: 'Demasiados pedidos. Tente daqui a pouco.',
    UPSTREAM_UNAVAILABLE: 'Um serviço externo não está disponível. Tente mais tarde.',
    NOT_CONFIGURED: 'Esta funcionalidade ainda não está ativa.',
    PLAN_LIMIT: 'Isto não faz parte do seu plano.',
    EXTENSION_OUTDATED: 'Esta versão da extensão já não é suportada. Atualize a extensão.',
    APP_OUTDATED: 'Esta versão da app de Mac já não é suportada. Atualize a app.',
    INTERNAL: 'Algo correu mal. Tente outra vez.',
  },
  savingsClaim:
    'Estimativa: 2 min poupados por passo documentado (referência: 120 s por passo feito à mão).',
  stepN: (n) => `Passo ${n}`,
};

const TEXTS: Record<Locale, Texts> = { en: EN, 'pt-PT': PT };
const tx = (l: Locale) => TEXTS[l] ?? EN;

export const roleLabel = (r: Role, l: Locale) => tx(l).roles[r];
export const planLabel = (p: PlanId, l: Locale) => tx(l).plans[p];
export const planLimitMessage = (r: PlanLimitReason, l: Locale) => tx(l).planLimits[r];
export const runLabel = (m: RunMark, l: Locale) => tx(l).runMarks[m];
export const datePresetLabel = (p: DatePreset, l: Locale) => tx(l).datePresets[p];
export const dateFormatLabel = (f: DateFormat, l: Locale) => tx(l).dateFormats[f];
export const errorMessage = (c: ErrorCode, l: Locale) => tx(l).errors[c] ?? tx(l).errors.INTERNAL;
export const savingsClaim = (l: Locale) => tx(l).savingsClaim;
export const stepLabel = (n: number, l: Locale) => tx(l).stepN(n);

/** 120 → «2 min»; 3600 → «1 h»; 5400 → «1 h 30 min»; 36000 → «10 h». */
export function formatSaved(seconds: number, l: Locale): string {
  const min = Math.round(seconds / 60);
  if (min < 60) return min + ' min';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return formatNumber(l, h) + ' h' + (m ? ' ' + m + ' min' : '');
}
